// Opt-in memory for the Character Studio's review settings.
//
// Every open of the studio used to start from scratch: first character in the
// roster, studio lighting, spin on, clay off. For someone reviewing one
// character over and over that is a set of clicks per open. With a
// `persistKey`, the studio keeps the handful of choices that describe *how*
// you like to look at a character (which one, lighting, spin, clay, ground,
// preview mode, playback rate) in `localStorage` under that key.
//
// Deliberately not remembered: the pose, bone edits, the clip and its time.
// Those are the work itself, and silently reopening onto a half-finished edit
// or a clip mid-scrub is more confusing than helpful.
//
// Storage access is wrapped throughout: `localStorage` throws outright in some
// contexts (blocked site data, some sandboxed iframes), and a studio that
// cannot remember must still open.

import type { CharacterPreviewMode } from './characterStudioStore';
import type { StageLighting } from './CharacterStudio';

/** What `persistKey` remembers. Every field is optional: absent means "not stored". */
export interface CharacterStudioPreferences {
  characterId?: string;
  lighting?: StageLighting;
  spin?: boolean;
  clay?: boolean;
  ground?: boolean;
  previewMode?: CharacterPreviewMode;
  playbackRate?: number;
}

const LIGHTING: readonly StageLighting[] = ['studio', 'torch', 'silhouette'];
const PREVIEW: readonly CharacterPreviewMode[] = ['stage', 'scene'];
const VERSION = 1;

/** True when `value` is one of the studio's lighting presets. */
export function isStageLighting(value: unknown): value is StageLighting {
  return typeof value === 'string' && (LIGHTING as readonly string[]).includes(value);
}

function defaultStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** Keep only well-typed fields from whatever was found in storage. */
export function sanitiseCharacterStudioPreferences(raw: unknown): CharacterStudioPreferences {
  if (!raw || typeof raw !== 'object') return {};
  const source = raw as Record<string, unknown>;
  const out: CharacterStudioPreferences = {};
  if (typeof source.characterId === 'string' && source.characterId) out.characterId = source.characterId;
  if (isStageLighting(source.lighting)) out.lighting = source.lighting;
  if (typeof source.spin === 'boolean') out.spin = source.spin;
  if (typeof source.clay === 'boolean') out.clay = source.clay;
  if (typeof source.ground === 'boolean') out.ground = source.ground;
  if (typeof source.previewMode === 'string' && (PREVIEW as readonly string[]).includes(source.previewMode)) {
    out.previewMode = source.previewMode as CharacterPreviewMode;
  }
  if (typeof source.playbackRate === 'number' && Number.isFinite(source.playbackRate) && source.playbackRate > 0) {
    out.playbackRate = source.playbackRate;
  }
  return out;
}

/**
 * Read the preferences stored under `key`. Returns `{}` when nothing is
 * stored, the stored value is unreadable, or storage is unavailable.
 */
export function readCharacterStudioPreferences(
  key: string,
  storage: Storage | null = defaultStorage(),
): CharacterStudioPreferences {
  if (!key || !storage) return {};
  try {
    const text = storage.getItem(key);
    if (!text) return {};
    const parsed = JSON.parse(text) as unknown;
    return sanitiseCharacterStudioPreferences(parsed);
  } catch {
    return {};
  }
}

/**
 * Store `preferences` under `key`, replacing what was there. Returns false
 * when storage is unavailable or refuses the write (quota, blocked); the
 * studio carries on either way.
 */
export function writeCharacterStudioPreferences(
  key: string,
  preferences: CharacterStudioPreferences,
  storage: Storage | null = defaultStorage(),
): boolean {
  if (!key || !storage) return false;
  try {
    storage.setItem(key, JSON.stringify({ v: VERSION, ...sanitiseCharacterStudioPreferences(preferences) }));
    return true;
  } catch {
    return false;
  }
}

// `persistKey`s that have already mounted a studio in this page. The first
// mount lets `initial*` props win over stored values (a deep link means "open
// on this"); later mounts, such as reopening the overlay, let the stored
// values win (they are what the user last chose). Reading and recording are
// separate so the read can happen during render (pure, safe to repeat under
// StrictMode's double render) and the record in an effect after commit.
const mountedKeys = new Set<string>();

/** True when a studio using `key` has already mounted in this page. */
export function hasStudioMountedWithKey(key: string): boolean {
  return mountedKeys.has(key);
}

/** Record that a studio using `key` has mounted. */
export function markStudioMountedWithKey(key: string): void {
  mountedKeys.add(key);
}

/** Forget every recorded mount. Tests only. */
export function resetStudioMountsForTests(): void {
  mountedKeys.clear();
}

/** Inputs to `resolveInitialStudioState`. */
export interface InitialStudioStateInput {
  characters: readonly { id: string; rigId: string }[];
  /** Clip ids per rig id, as the studio's `clips` prop would give them. */
  clipIdsByRig?: Readonly<Record<string, readonly string[]>>;
  initialCharacterId?: string;
  initialClipId?: string;
  initialLighting?: StageLighting;
  /** What `readCharacterStudioPreferences` returned (`{}` without a key). */
  stored: CharacterStudioPreferences;
  /** Whether `initial*` props take precedence over `stored` on this mount. */
  propsFirst: boolean;
  /** Playback rates the transport offers. */
  playbackRates: readonly number[];
  /**
   * The viewer's system asks for reduced motion. Only changes the default:
   * spin starts off instead of on. A stored spin choice still wins.
   */
  prefersReducedMotion?: boolean;
}

/** Resolved starting state for one mount of the studio. */
export interface InitialStudioState {
  characterId: string;
  clipId: string | null;
  lighting: StageLighting;
  spin: boolean;
  clay: boolean;
  ground: boolean;
  previewMode: CharacterPreviewMode | null;
  playbackRate: number;
  /** Human-readable reasons an `initial*` prop was ignored. */
  warnings: string[];
}

/**
 * Work out where a studio should open. Per field, the first usable value
 * wins, in this order:
 *
 * - `propsFirst` (the first mount with a `persistKey` in this page, or every
 *   mount without one): the `initial*` prop, then the stored value, then the
 *   built-in default.
 * - otherwise (reopening with a `persistKey`): the stored value, then the
 *   `initial*` prop, then the built-in default.
 *
 * `initialClipId` has no stored counterpart and is only applied when props
 * come first, and only if it names a clip for the resolved character's rig.
 * An `initial*` prop naming something that does not exist is ignored with a
 * warning; a stale stored value is ignored silently, since a roster changing
 * under a remembered choice is expected.
 */
export function resolveInitialStudioState(input: InitialStudioStateInput): InitialStudioState {
  const { characters, stored, propsFirst } = input;
  const warnings: string[] = [];
  const knownCharacter = (id: string | undefined): id is string => !!id && characters.some((c) => c.id === id);

  // An empty roster may simply not have arrived yet: keep a requested id
  // rather than warn, and let the studio's roster check settle it later.
  if (input.initialCharacterId !== undefined && characters.length > 0 && !knownCharacter(input.initialCharacterId)) {
    warnings.push(
      `initialCharacterId "${input.initialCharacterId}" is not in characters (${characters.map((c) => c.id).join(', ')}); using the default.`,
    );
  }
  const characterOrder = propsFirst
    ? [input.initialCharacterId, stored.characterId]
    : [stored.characterId, input.initialCharacterId];
  const characterId =
    characterOrder.find(knownCharacter) ??
    (characters.length > 0 ? characters[0]!.id : (input.initialCharacterId ?? stored.characterId ?? ''));

  if (input.initialLighting !== undefined && !isStageLighting(input.initialLighting)) {
    warnings.push(`initialLighting "${String(input.initialLighting)}" is not one of ${LIGHTING.join(', ')}; using the default.`);
  }
  const lightingOrder = propsFirst ? [input.initialLighting, stored.lighting] : [stored.lighting, input.initialLighting];
  const lighting = lightingOrder.find(isStageLighting) ?? 'studio';

  let clipId: string | null = null;
  if (input.initialClipId !== undefined && propsFirst) {
    const rigId = characters.find((c) => c.id === characterId)?.rigId;
    const available = (rigId && input.clipIdsByRig?.[rigId]) || [];
    if (available.includes(input.initialClipId)) clipId = input.initialClipId;
    else if (characters.length > 0) {
      warnings.push(
        `initialClipId "${input.initialClipId}" is not a clip for character "${characterId}"${rigId ? ` (rig "${rigId}")` : ''}; opening with no clip.`,
      );
    }
  }

  const playbackRate =
    stored.playbackRate !== undefined && input.playbackRates.includes(stored.playbackRate) ? stored.playbackRate : 1;

  return {
    characterId,
    clipId,
    lighting,
    spin: stored.spin ?? !input.prefersReducedMotion,
    clay: stored.clay ?? false,
    ground: stored.ground ?? true,
    previewMode: stored.previewMode ?? null,
    playbackRate,
    warnings,
  };
}
